import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorText, post } from '../lib/api';
import { queryClient, useMe, useProjects } from '../lib/queries';
import { Icon, StatusIcon } from '../ui/icons';
import { Button, Checkbox } from '../ui/primitives';
import { ProjectFormModal } from './projects/ProjectFormModal';
import './onboarding.css';

// 首次使用 · 确认负责的项目（设计稿 11a）

export function OnboardingPage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: projects = [] } = useProjects();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (me?.onboarded) navigate('/', { replace: true });
  }, [me, navigate]);

  const active = projects.filter((p) => p.status !== 'done' && p.status !== 'cancelled');
  // 推荐：我已参与的、同团队的项目
  const recommended = useMemo(
    () => active.filter((p) => p.mine || (me?.teamId && p.teamId === me.teamId)),
    [active, me?.teamId],
  );
  const others = active.filter((p) => !recommended.includes(p));

  useEffect(() => {
    setPicked((s) => new Set([...s, ...active.filter((p) => p.mine).map((p) => p.id)]));
  }, [projects.length]);

  const toggle = (id: string) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await post('/me/onboarding', { projectIds: [...picked] });
      await queryClient.invalidateQueries();
      navigate('/', { replace: true });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const row = (p: (typeof active)[number]) => (
    <button key={p.id} type="button" className={clsx('ob-row', picked.has(p.id) && 'on')} onClick={() => toggle(p.id)}>
      <Checkbox checked={picked.has(p.id)} large />
      <span className="info">
        <span className="title">
          <span className="name">{p.name}</span>
          <span className="code">{p.code}</span>
        </span>
        <span className="desc">
          {p.ownerName}负责{p.mine ? ' · 你参与' : ''}
        </span>
      </span>
      <StatusIcon status={p.status} />
    </button>
  );

  return (
    <div className="ob">
      <div className="ob-body">
        <div className="ob-head">
          <div className="title">欢迎，{me?.name ?? ''}</div>
          <div className="meta">
            {me?.teamName ?? me?.title ?? ''} · 第 1 步，共 2 步
          </div>
          <p className="lead">先确认你负责的项目。之后每天的日报会按这些项目预填，你只需要改几句。</p>
        </div>
        {active.length === 0 ? (
          <div className="ob-label">工作区里还没有项目，先新建一个</div>
        ) : (
          <div className="ob-label">根据你的团队推荐</div>
        )}
        {recommended.map(row)}
        {showAll ? (
          <>
            <div className="ob-label">其他项目</div>
            {others.map(row)}
          </>
        ) : (
          others.length > 0 && (
            <button type="button" className="ob-row add" onClick={() => setShowAll(true)}>
              <Icon name="plus" size={18} />
              <span className="name c3">添加其他项目</span>
            </button>
          )
        )}
        <button type="button" className="ob-row add" onClick={() => setCreating(true)}>
          <Icon name="plus" size={18} />
          <span className="name c3">没找到你的项目？新建一个</span>
        </button>
        {creating && (
          <ProjectFormModal
            onClose={() => setCreating(false)}
            onCreated={(r) => setPicked((s) => new Set([...s, r.id]))}
          />
        )}
        <p className="ob-tip">以后随时可以在「我 › 负责的项目」里调整。</p>
        {error && <p className="ob-tip c-red">{error}</p>}
      </div>
      <div className="ob-foot">
        <Button mobile variant="primary" full disabled={picked.size === 0 || busy} onClick={() => void submit()}>
          下一步：写第一份日报
        </Button>
      </div>
    </div>
  );
}
